// dependencies
import React from "react";
import { useLocation, useSearchParams } from "react-router-dom";

// icons
import { faPaperPlane, faSliders } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCheckCircle, faEllipsis, faShare } from "@performyard/fontawesome/pro-solid-svg-icons";
// py-components
import {
	ButtonIcon,
	ButtonStandard,
	DropDownMenu,
	DropDownMenuOption,
	Link,
	Spinner,
	Text as Typography,
} from "@performyard/py-components";

// components
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import TruncatedText from "../../../../../components/Text/TruncatedText";
import Tooltip from "../../../../../components/Tooltip";

import {
	useHasChanges,
	useOrderedQuestions,
	useActions as useSurveyBuilderActions,
} from "../pages/SurveyBuilder/store/store";
import { useSurveyAccess } from "../pages/SurveyParticipants/store/store";

import { SURVEY_DETAILS_MODALS } from "../store/model";
// store
import { useSetters as useSurveyDetailsSetters } from "../store/store";

import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";
// api
import { useSurvey } from "../hooks/useSurvey";

import { searchParamsSchema } from "../SurveyDetails";
// config
import { surveyDetailsConfig } from "../config";
// styles
import css from "./Navigation.module.scss";

const { routes } = surveyDetailsConfig;

const NavigationButtons = ({ surveyId, onPublishClick }: { surveyId: string; onPublishClick?: () => void }) => {
	const surveyBuilderActions = useSurveyBuilderActions();
	const surveyDetailsSetters = useSurveyDetailsSetters();
	const surveyAccess = useSurveyAccess();
	const hasChanges = useHasChanges();
	const orderedQuestions = useOrderedQuestions();
	const { survey, storeSurvey, isLoading } = useSurvey({ surveyId });
	const { queryParams: searchParams } = useSearchQueryParams({
		schema: searchParamsSchema,
	});
	const tab = searchParams.tab;

	const isPublished = survey?.status !== "draft";

	const handleClickCopySurvey = () => {
		if (
			(survey?.participantSelection?.enabled || surveyAccess === "selected-employees") &&
			!survey?.participantSelection?.selectionObjectId
		) {
			surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.NO_PARTICIPANTS_SELECTED_MODAL);
			return;
		}
		surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.SAVE_AS_COPY);
	};

	const handleClickCloseSurvey = () => {
		surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.CLOSE_SURVEY);
	};

	const handleClickDeleteSurvey = () => {
		surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.DELETE_SURVEY);
	};
	const handlePublishSurvey = () => {
		if (isPublished) {
			return;
		}

		if (
			(survey?.participantSelection?.enabled || surveyAccess === "selected-employees") &&
			!survey?.participantSelection?.selectionObjectId
		) {
			surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.NO_PARTICIPANTS_SELECTED_MODAL);
			return;
		}

		// If we have a publish callback, use it (for form validation)
		// only work if the tab is build...
		if (onPublishClick && tab === "build") {
			onPublishClick();
			return;
		}

		// Fallback to original logic if no callback provided
		if (orderedQuestions.length === 0) {
			// if the user navigated away, the survey builder store may have been cleared.
			// This populates the store with the current survey data, so the publish action
			// can validate and publish the survey.
			storeSurvey(survey);
		}

		surveyBuilderActions.validatePublishSurvey(
			(questions, name) => {
				surveyDetailsSetters.setValidQuestions(questions);
				surveyDetailsSetters.setValidName(name);
				surveyDetailsSetters.setModal("publish-survey");
			},
			() => {
				surveyDetailsSetters.setModal("publish-survey-invalid");
			}
		);
	};

	const dropdownOptions: DropDownMenuOption[] = [
		...(!hasChanges
			? [
					{
						key: "save-as-copy",
						label: "Save as Copy",
						onClick: handleClickCopySurvey,
					},
				]
			: []),
		...(survey?.status === "active" || survey?.status === "scheduled"
			? [
					{
						key: "close-survey",
						label: "Close Survey",
						onClick: handleClickCloseSurvey,
					},
				]
			: []),
		{
			key: "delete-survey",
			label: "Delete Survey",
			onClick: handleClickDeleteSurvey,
		},
	];

	if (isLoading) {
		return (
			<div style={{ pointerEvents: "none" }} className="flex h-12 items-center gap-2">
				<ButtonStandard variant="outline" color="primary" tabIndex={-1}>
					<Spinner />
				</ButtonStandard>
			</div>
		);
	}

	return (
		<>
			<div className="flex h-12 items-center justify-end gap-[12px]">
				{isPublished && survey?.status !== "closed" && (
					<Tooltip revealed message="Share Survey">
						<ButtonIcon
							icon={faShare}
							size={32}
							variant="outline"
							onClick={() => surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.SHARE_SURVEY)}
						/>
					</Tooltip>
				)}
				{!["closed", "draft"].includes(survey?.status ?? "") && (
					<Tooltip revealed message="Survey Settings">
						<ButtonIcon
							icon={faSliders}
							size={32}
							variant="outline"
							onClick={() => surveyDetailsSetters.setModal(SURVEY_DETAILS_MODALS.SURVEY_SETTINGS)}
						/>
					</Tooltip>
				)}

				<Tooltip revealed message="Survey Options">
					<DropDownMenu
						testid="survey-results-header-options-menu"
						options={dropdownOptions}
						className="flex items-center justify-center px-0"
						placement="bottom-end"
					>
						<ButtonIcon icon={faEllipsis} variant="outline" size={32} onClick={() => {}} />
					</DropDownMenu>
				</Tooltip>
				<div className="h-[31.5px] border-l-[1px] border-solid border-neutral-3"></div>
				{!isPublished && (
					<div className="flex h-12 items-center gap-2">
						<ButtonStandard
							variant="outline"
							iconLeft={faPaperPlane}
							color="primary"
							onClick={handlePublishSurvey}
						>
							Publish
						</ButtonStandard>
					</div>
				)}
				{isPublished && (
					<div style={{ pointerEvents: "none" }} className="flex h-12 items-center gap-2">
						<ButtonStandard iconLeft={faCheckCircle} color="primary" tabIndex={-1}>
							Published
						</ButtonStandard>
					</div>
				)}
			</div>
		</>
	);
};

/**
 * Renders a navigation bar for the survey details
 */
const Navigation = ({
	surveyName,
	surveyId,
	onPublishClick,
}: {
	surveyName: string;
	surveyId: string;
	onPublishClick?: () => void;
}) => {
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const tab = searchParams.get("tab");

	return (
		<div className="flex h-12 items-center justify-between gap-2 border-b-[1px] border-t-[1px] border-solid border-neutral-2 bg-white px-[56px]">
			{/* Breadcrumbs */}
			<div className="flex justify-start">
				<Breadcrumbs
					className="text-sm text-neutral-7"
					pages={[
						{
							label: "Surveys",
							url: "/surveys",
							pageID: "surveys",
						},
						{
							label: (
								<TruncatedText
									text={surveyName}
									maxWidth={300}
									size="paragraph-3"
									color="neutral-8"
									weight="semi-bold"
								/>
							),
							url: `surveys/${surveyId}`,
							pageID: tab ?? "survey-builder",
						},
					]}
				/>
			</div>
			{/* Navigation */}
			<div className="absolute left-1/2 -translate-x-1/2">
				<div className="flex gap-6">
					{Object.keys(routes).map((route) => {
						const { label, tab: routeTab } = routes[route];
						const fullUrl = `${location.pathname}?tab=${routeTab}`;
						const isActiveRoute = tab === routeTab;
						return (
							<div
								key={route}
								className={`flex items-center justify-center text-nowrap ${css.navOption} ${isActiveRoute ? css.active : ""} min-w-14`}
							>
								<Link underline="never" href={fullUrl}>
									<Typography
										size="paragraph-3"
										color={isActiveRoute ? "neutral-8" : "neutral-6"}
										weight="semi-bold"
									>
										{label}
									</Typography>
								</Link>
							</div>
						);
					})}
				</div>
			</div>
			{/* Actions */}
			<div className="flex justify-end">
				<NavigationButtons surveyId={surveyId} onPublishClick={onPublishClick} />
			</div>
		</div>
	);
};

export default Navigation;
